import { Component } from '@theme/component';

/**
 * Browser-persisted wishlist.
 *
 * Holds an array of product *handles* (not ids) in localStorage so the wishlist
 * page can re-render each saved product server-side via a `?view=` fragment,
 * keeping prices in Liquid. Every mutation dispatches a document-level
 * `CHANGE_EVENT` so the heart buttons, header counter and page stay in sync,
 * and a native `storage` listener keeps tabs in sync too.
 */
const STORAGE_KEY = 'oj:wishlist';
const CHANGE_EVENT = 'oj:wishlist:change';

export class Wishlist {
  /** @returns {string[]} */
  static read() {
    try {
      const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  /** @param {string[]} handles */
  static write(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      /* storage may be full or blocked — fail quietly */
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
  }

  /** @param {string} handle */
  static has(handle) {
    return this.read().includes(handle);
  }

  /** @param {string} handle */
  static add(handle) {
    if (!handle) return;
    const handles = this.read();
    if (!handles.includes(handle)) {
      handles.unshift(handle);
      this.write(handles);
    }
  }

  /** @param {string} handle */
  static remove(handle) {
    this.write(this.read().filter((existing) => existing !== handle));
  }

  /** @param {string} handle @returns {boolean} now active */
  static toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  }

  static get count() {
    return this.read().length;
  }
}

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles: Wishlist.read() } }));
  }
});

/**
 * Heart toggle. Markup:
 * <wishlist-button data-handle="…"><button on:click="/toggle" aria-pressed>…</button></wishlist-button>
 */
class WishlistButton extends Component {
  #onChange = () => this.#sync();

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener(CHANGE_EVENT, this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  get handle() {
    return this.getAttribute('data-handle') || '';
  }

  /** @param {Event} [event] */
  toggle(event) {
    // Heart lives next to (never inside) the card link — guard anyway so a
    // click never follows the card or triggers a view transition.
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    Wishlist.toggle(this.handle);
    this.#sync();
  }

  #sync() {
    const active = Wishlist.has(this.handle);
    this.classList.toggle('is-active', active);
    const button = this.querySelector('button');
    if (!button) return;
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }
}

/**
 * Header counter. Shows the number of saved items, hidden when empty.
 */
class WishlistIcon extends Component {
  #onChange = () => this.#render();

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(CHANGE_EVENT, this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  #render() {
    const count = Wishlist.count;
    const bubble = this.querySelector('[data-wishlist-count]');
    if (bubble) bubble.textContent = count > 99 ? '99+' : String(count);
    this.classList.toggle('is-empty', count === 0);
    this.setAttribute('data-count', String(count));
  }
}

/**
 * Wishlist page. Reads saved handles and renders each as a server-rendered
 * product-card fragment (prices stay in Liquid). Prunes handles that 404.
 */
class WishlistPage extends Component {
  #onChange = () => this.render();
  #rendering = false;

  connectedCallback() {
    super.connectedCallback();
    this.render();
    document.addEventListener(CHANGE_EVENT, this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  get view() {
    return this.getAttribute('data-view') || 'wishlist-card';
  }

  async render() {
    if (this.#rendering) return;
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const total = this.querySelector('[data-wishlist-total]');
    if (!grid) return;

    const handles = Wishlist.read();
    if (total) total.textContent = String(handles.length);

    if (handles.length === 0) {
      grid.innerHTML = '';
      this.classList.add('is-empty');
      if (empty) empty.hidden = false;
      return;
    }

    this.#rendering = true;
    this.classList.remove('is-empty');
    if (empty) empty.hidden = true;
    this.setAttribute('aria-busy', 'true');

    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${handle}?view=${this.view}`);
          if (!response.ok) return { handle, html: null };
          return { handle, html: (await response.text()).trim() };
        } catch (error) {
          return { handle, html: null };
        }
      })
    );

    const valid = results.filter((result) => result.html);
    grid.innerHTML = valid.map((result) => result.html).join('');
    if (total) total.textContent = String(valid.length);
    this.setAttribute('aria-busy', 'false');
    this.#rendering = false;

    // Drop any handles whose product no longer resolves. write() re-dispatches
    // change → one more render that finds nothing to prune, then settles.
    if (valid.length !== handles.length) {
      const keep = new Set(valid.map((result) => result.handle));
      Wishlist.write(handles.filter((handle) => keep.has(handle)));
    }
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-icon')) customElements.define('wishlist-icon', WishlistIcon);
if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
